/**
 * Releases, pins and the rollout (container, D15): the panel, the pin dialog
 * (stage or site), and clearing a pin. Live through `releasePin` changes and
 * the vendor mirror (a node reporting new packages moves the rollout).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import type { PinTarget } from '../api/vendor.js';
import { useChangePin, useVendorReleases } from '../app/hooks.js';
import { PinDialog, type PinDialogTarget } from '../components/vendor/PinDialog.js';
import { ReleasesPanel } from '../components/vendor/ReleasesPanel.js';
import { useVendorAdmin } from './VendorLayout.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function targetText(target: PinTarget): string {
  return target.kind === 'stage' ? 'the stage' : `site ${target.siteId}`;
}

export function VendorReleasesView() {
  const releases = useVendorReleases();
  const change = useChangePin();
  const canAdmin = useVendorAdmin();
  const [pinning, setPinning] = useState<PinDialogTarget | null>(null);
  const data = releases.data ?? null;
  const current = pinning === null || data === null ? null : pinning.kind === 'stage' ? (data.stagePin?.listHash ?? null) : (data.sitePins.find((p) => p.siteId === pinning.siteId)?.pin.listHash ?? null);
  const clear = (target: PinTarget) =>
    change.mutate(
      { target, action: { kind: 'clear' } },
      {
        onSuccess: () => toast.success(`Cleared the pin of ${targetText(target)}`),
        onError: (err) => toast.error(`Not cleared: ${messageOf(err)}`),
      },
    );
  return (
    <>
      <ReleasesPanel
        releases={data}
        loading={releases.isFetching}
        error={releases.error === null ? null : messageOf(releases.error)}
        canAdmin={canAdmin}
        onRetry={() => void releases.refetch()}
        onPinStage={() => {
          change.reset();
          setPinning({ kind: 'stage' });
        }}
        onPinSite={(siteId) => {
          change.reset();
          setPinning({ kind: 'site', siteId });
        }}
        onClearStage={() => clear({ kind: 'stage' })}
        onClearSite={(siteId) => clear({ kind: 'site', siteId })}
      />
      <PinDialog
        target={pinning}
        releases={data?.releases ?? []}
        current={current}
        pending={change.isPending}
        error={change.error === null ? null : messageOf(change.error)}
        onClose={() => setPinning(null)}
        onSubmit={({ target, listHash, note }) =>
          change.mutate(
            { target, action: { kind: 'set', listHash, note } },
            {
              onSuccess: () => {
                setPinning(null);
                toast.success(`Pinned ${targetText(target)}`);
              },
            },
          )
        }
      />
    </>
  );
}
